// ═══════════════════════════════════════════════════════════════════════════════
//  src/utils/browserSpeech.js
//  Síntesis de voz del navegador — utilidad compartida
//
//  Web Speech API nativa: arranca en milisegundos, no cuesta nada y no toca el
//  pipeline de TTS del tutor (`tts.py`, Zona Roja del Protocolo Central).
//
//  Nota de linaje: `components/onboarding/onboardingSpeech.js` hace lo mismo y
//  es anterior a este archivo. Se dejó en su sitio a propósito — funciona y
//  está en producción. Cuando convenga puede migrar aquí; mientras tanto este
//  módulo sirve al Assessment sin crear una dependencia entre microservicios.
// ═══════════════════════════════════════════════════════════════════════════════

let cachedVoice = null;

function getEnglishVoice() {
  if (cachedVoice) return cachedVoice;
  const voices = window.speechSynthesis?.getVoices() ?? [];
  cachedVoice =
    voices.find((v) => v.name === 'Google UK English Female') ||
    voices.find((v) => v.lang === 'en-GB' && v.name.toLowerCase().includes('female')) ||
    voices.find((v) => v.lang === 'en-GB') ||
    voices.find((v) => v.lang?.startsWith('en-')) ||
    null;
  return cachedVoice;
}

if (typeof window !== 'undefined' && window.speechSynthesis) {
  // Chrome carga las voces de forma asíncrona.
  window.speechSynthesis.onvoiceschanged = () => { cachedVoice = null; };
  window.speechSynthesis.getVoices();
}

export function isSpeechAvailable() {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/**
 * Reproduce un texto en inglés.
 * @param {string} text
 * @param {{rate?:number, pitch?:number}} voice  transmite la intención del hablante
 * @param {{onStart?:Function, onEnd?:Function, onError?:Function}} cb
 */
export function speak(text, voice = {}, { onStart, onEnd, onError } = {}) {
  if (!isSpeechAvailable() || !text) {
    onError?.('speech-unavailable');
    return;
  }

  window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'en-GB';
  utterance.rate = voice.rate ?? 0.9;
  utterance.pitch = voice.pitch ?? 1.0;
  utterance.volume = 1.0;

  const v = getEnglishVoice();
  if (v) utterance.voice = v;

  utterance.onstart = () => onStart?.();
  utterance.onend = () => onEnd?.();
  utterance.onerror = (e) => onError?.(e?.error ?? 'speech-error');

  // Chrome recolecta la utterance si nadie la referencia.
  window._nslSpeechRef = utterance;
  window.speechSynthesis.speak(utterance);
}

export function cancelSpeech() {
  if (isSpeechAvailable()) window.speechSynthesis.cancel();
}

/* ═══════════════════════════════════════════════════════════════════════════
   RECONOCIMIENTO DE VOZ (STT)

   Transcribe en el propio dispositivo, con resultados parciales en vivo.
   Por eso el Assessment NO necesita WebSockets para medir habla: el navegador
   ya hace el streaming localmente, a 0 ms y sin costo. Un socket contra
   nuestro backend sería estrictamente peor.

   ⚠️  LÍMITE DEL INSTRUMENTO: el reconocedor NORMALIZA el acento — devuelve
       palabras bien escritas aunque la pronunciación sea imperfecta. Sirve
       para medir producción (riqueza, coherencia, fluidez), NO pronunciación.
   ═══════════════════════════════════════════════════════════════════════════ */

export function isRecognitionAvailable() {
  return typeof window !== 'undefined' &&
    Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);
}

/**
 * Crea un reconocedor continuo para tareas de speaking.
 *
 * @param {Object} cb
 *   onPartial(textoAcumulado)  se dispara mientras habla
 *   onFinal(textoAcumulado)    al detenerse
 *   onError(codigo)
 * @returns {{start:Function, stop:Function}|null}
 */
export function createRecognition({ onPartial, onFinal, onError } = {}) {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) {
    onError?.('unsupported');
    return null;
  }

  const rec = new SR();
  rec.lang = 'en-US';
  rec.continuous = true;        // la tarea dura hasta 60 s
  rec.interimResults = true;    // texto en vivo mientras habla
  rec.maxAlternatives = 1;

  let settled = '';   // fragmentos ya definitivos
  let stopped = false;

  rec.onresult = (event) => {
    let interim = '';
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const chunk = event.results[i][0].transcript;
      if (event.results[i].isFinal) settled += `${chunk} `;
      else interim += chunk;
    }
    onPartial?.(`${settled}${interim}`.replace(/\s+/g, ' ').trim());
  };

  rec.onerror = (ev) => {
    // 'no-speech' y 'aborted' son normales al cortar: no son fallos reales.
    if (ev.error !== 'no-speech' && ev.error !== 'aborted') onError?.(ev.error);
  };

  rec.onend = () => {
    if (!stopped) {
      // Chrome corta solo tras unos segundos de silencio. Se reanuda para que
      // el alumno no pierda la tarea por hacer una pausa para pensar.
      try { rec.start(); return; } catch { /* ya detenido */ }
    }
    onFinal?.(settled.replace(/\s+/g, ' ').trim());
  };

  return {
    start() {
      settled = '';
      stopped = false;
      try { rec.start(); } catch (e) { onError?.(String(e)); }
    },
    stop() {
      stopped = true;
      try { rec.stop(); } catch { /* ya detenido */ }
    },
  };
}
